// Isolated HTTP client for the Track 2 agentic backend (Azure Function App).
//
// Auth model (as of 2026-06-02 — one auth system across Track 1 + Track 2):
//   1. User signs in once via Track 1's Entra ID flow (BFF sets an HttpOnly
//      session cookie on *.horne-dev.com).
//   2. This client calls the BFF `/api/v1/auth/agentic-token` endpoint with
//      `credentials:'include'` to mint a short-lived (~15 min) HS256 JWT.
//   3. The JWT is attached as `Authorization: Bearer <jwt>` on every call
//      to the agentic Function App. The FA validates it against the shared
//      KV-backed signing key (AGENTIC_JWT_SIGNING_SECRET).
//   4. `x-functions-key` is kept as defense-in-depth at the platform edge;
//      we'll drop it once JWT auth is proven stable (see OPEN_ITEMS.md).
//
// The second MSAL flow that previously gated /apps is gone. Do NOT import
// `@azure/msal-*` from this file.
//
// On a 401 from the FA we surface a scoped toast — we don't dispatch
// SESSION_EXPIRED_EVENT or call `/api/v1/auth/refresh` here (the Track 1
// session is the source of truth; if it expired, Track 1's own flow handles
// the redirect).
//
// We continue to dispatch the 'api-activity' window event so Track 1's
// inactivity timer stays reset while the user is actively using agentic
// features.
import { toast } from 'react-toastify';
import { getBffBaseUrl } from './bffBaseUrl';
import { getCsrfToken } from './csrfToken';


const API_ACTIVITY_EVENT = 'api-activity';

const getEnv = (key) => window._env_?.[key] ?? process.env[key];

const getBaseUrl = () => getEnv('REACT_APP_AGENTIC_API_BASE_URL') || '';

// Track 1 BFF base URL — used to mint agentic tokens. Lives on the same
// parent domain (horne-dev.com) as the SPA, so the session cookie travels
// when we add `credentials: 'include'`.
//
const getBaseHeaders = () => {
  const headers = { 'Content-Type': 'application/json' };
  const functionKey = getEnv('REACT_APP_AGENTIC_API_FUNCTION_KEY');
  if (functionKey) headers['x-functions-key'] = functionKey;
  return headers;
};

const appendCode = (url) => {
  const code = getEnv('REACT_APP_AGENTIC_API_CODE');
  if (!code) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}code=${encodeURIComponent(code)}`;
};

// ─────────────────────────────────────────────────────────────────────────
// Agentic JWT cache
//
// Module-scoped (singleton). Refreshes proactively ~60s before expiry so a
// long agentic SSE stream never hits the FA with a token that just expired.
// On hard failure we surface a one-line toast and let the user retry — the
// Track 1 session cookie is likely the underlying problem.
// ─────────────────────────────────────────────────────────────────────────
const REFRESH_BUFFER_MS = 60 * 1000;

let cachedToken = null;
let cachedExpiresAt = 0; // epoch millis
let inFlightFetch = null;

const _now = () => Date.now();

const _isFresh = () =>
  cachedToken && cachedExpiresAt - _now() > REFRESH_BUFFER_MS;

const _tryRefreshTrack1Session = async (bffBase, csrfToken) => {
  try {
    const r = await fetch(`${bffBase}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
      },
    });
    return r.ok;
  } catch {
    return false;
  }
};

const _mintOnce = async (bffBase, csrfToken) => {
  const response = await fetch(`${bffBase}/api/v1/auth/agentic-token`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
    },
  });
  return response;
};

const _fetchAgenticToken = async () => {
  const bffBase = getBffBaseUrl();
  if (!bffBase) {
    throw new Error(
      'agenticApiClient: REACT_APP_API_BASE_URL is not configured; cannot mint agentic token',
    );
  }

  // Pull csrf_token from the normal first-party cookie path. For localhost
  // against the deployed dev BFF, fall back to the dev-only csrf-token endpoint
  // because localhost cannot read .horne-dev.com cookies.
  const csrfToken = await getCsrfToken(bffBase);

  let response = await _mintOnce(bffBase, csrfToken);

  // If the BFF rejected due to an expired Entra access_token, attempt one
  // silent Track 1 refresh and retry. The backend fix (skip_token_validation)
  // handles the common case; this covers the rarer 2h access_token cookie expiry.
  if (response.status === 401) {
    const refreshed = await _tryRefreshTrack1Session(bffBase, csrfToken);
    if (refreshed) {
      const freshCsrf = await getCsrfToken(bffBase);
      response = await _mintOnce(bffBase, freshCsrf);
    }
  }

  if (!response.ok) {
    cachedToken = null;
    cachedExpiresAt = 0;
    const text = await response.text().catch(() => '');
    throw new Error(
      `agenticApiClient: token mint failed (HTTP ${response.status}): ${text.slice(0, 200)}`,
    );
  }

  const data = await response.json();
  if (!data || !data.token || !data.expiresAt) {
    cachedToken = null;
    cachedExpiresAt = 0;
    throw new Error('agenticApiClient: token mint returned malformed payload');
  }

  cachedToken = data.token;
  cachedExpiresAt = Date.parse(data.expiresAt);
  if (Number.isNaN(cachedExpiresAt)) {
    // Fall back to a conservative 10-minute lifetime if the BFF returns an
    // unparseable timestamp (shouldn't happen, but don't poison the cache).
    cachedExpiresAt = _now() + 10 * 60 * 1000;
  }
  return cachedToken;
};

export const getAgenticToken = async () => {
  if (_isFresh()) return cachedToken;
  if (!inFlightFetch) {
    inFlightFetch = _fetchAgenticToken().finally(() => {
      inFlightFetch = null;
    });
  }
  return inFlightFetch;
};

const attachBearerToken = async (headers) => {
  // Fail closed: if we can't mint a valid user bearer we must NOT fall back to
  // sending the request on the static function key alone. The FA now *requires*
  // the JWT, and a silent function-key-only request would (a) be rejected by
  // the FA anyway and (b) mask a logged-out/expired session — the exact bug
  // where a sibling tab kept "working" after logout. Let the mint error
  // propagate so agenticFetch can surface it as a clear auth failure.
    const token = await getAgenticToken();
  return { ...headers, Authorization: `Bearer ${token}` };
};

const agenticFetch = async (path, options = {}) => {
  window.dispatchEvent(new Event(API_ACTIVITY_EVENT));

  const baseUrl = getBaseUrl();
  const url = appendCode(`${baseUrl}${path}`);

  let headers;
  try {
    headers = await attachBearerToken({ ...getBaseHeaders(), ...(options.headers || {}) });
  } catch (err) {
    // Token mint failed → no live session. Fail closed with the same UX as a
    // 401 rather than firing an unauthenticated request at the FA.
    cachedToken = null;
    cachedExpiresAt = 0;
    // eslint-disable-next-line no-console
    console.warn('agenticApiClient: bearer token unavailable, failing closed', err);
    toast.error('Agentic session expired. Please log in again.');
    throw new Error('Agentic API: session unavailable (token mint failed)');
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    // Invalidate the cached token so the next call re-mints. The most likely
    // cause is the underlying Track 1 session timing out between BFF mint and
    // FA use; re-minting will either succeed (clock skew / mid-flight expiry)
    // or 401 at the BFF, which we surface clearly.
    cachedToken = null;
    cachedExpiresAt = 0;
    toast.error('Agentic session expired. Please log in again.');
    throw new Error('Agentic API: 401 Unauthorized');
  }

  if (!response.ok) {
    throw new Error(`Agentic API error: ${response.status} ${response.statusText}`);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

export const agenticPostStream = async (path, body, {onEvent}={})=>{
  window.dispatchEvent(new Event(API_ACTIVITY_EVENT));

  const url = appendCode(`${getBaseUrl()}${path}`);
  let headers;
  try {
    headers = await attachBearerToken({
      ...getBaseHeaders(),
      Accept: 'text/event-stream',
    });
  }catch(err) {
    cachedToken =null;
    cachedExpiresAt =0;

    console.warn('agenticApiClient: bearer token unabailablem failing closed', err);
    toast.error('Agentic session expired. Please log in again.');
    throw new Error('Agentic API: session unavilable (token mint failed');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({...body, stream: true}),
  });

  if (response.status === 401){
    cachedToken =null;
    cachedExpiresAt =0;
    toast.error('Agentic session expired. Please log in again.');
    throw new Error('Agentic API: 401 Unauthorized');

  }
  if (!response.ok || !response.body){
    throw new Error(`Agentic API error: ${response.status} ${response.statusText}`);
  }

  const reader = response.body.getReader();
  const decoder =  new TextDecoder();
  let buffer = '';
  let finalPayload = null;

  while (true) {
    const {value, done} = await reader.read();
      if(done) {
        break;
      }
      buffer += decoder.decode(value, {stream:true});

      const frames = buffer.split('\n\n');
      buffer = frames.pop() || '';
      for (const frame of frames){
        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
        if(!dataLine) continue;
        let payload;
        try{
          payload = JSON.parse(dataLine.slice(5).trim());
        }catch{
          continue;
        }
        if (payload.type ==='error'){
          throw new Error(payload.error || 'Agentic stream error')
        }
        if (payload.type ==='final'){
          finalPayload =payload;
        }
        if (onEvent) {
          onEvent(payload);
        }
      }
    }
    if (!finalPayload){
      throw new Error ('Agentic API: Stream ended without a final event')
    }

    return {
      response: finalPayload.text,
      output_text: finalPayload.output_text,
      thread_id: finalPayload.thread_id,
    };
  };



export const pollProgress = (runId, sinceSeq=0) =>
  agenticGet(`/api/chat/progress/${runId}?since=${sinceSeq}`);

export const agenticGet = (path, options = {}) =>
  agenticFetch(path, { ...options, method: 'GET' });

export const agenticPost = (path, body, options = {}) =>
  agenticFetch(path, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
  });

export const agenticPut = (path, body, options = {}) =>
  agenticFetch(path, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
  });

// These agentic endpoints (status_update / dob_status_update) take their
// parameters in the query string and have no JSON body, so `body` is optional.
export const agenticPatch = (path, body, options = {}) =>
  agenticFetch(path, {
    ...options,
    method: 'PATCH',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

export const agenticDelete = (path, options = {}) =>
  agenticFetch(path, { ...options, method: 'DELETE' });

export default agenticFetch;
